import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactElement, ReactNode } from 'react';

export const NAMESPACE = 'components/trial/lp/v3b';
export const IMG = '/assets/images/trial/v3b';

/** The design's mono eyebrow with its lime dot. */
export const EYEBROW = 'inline-flex items-center gap-2 font-mono text-[.6875rem] font-bold tracking-[.14em] uppercase text-[#93FF18] before:size-1.5 before:rounded-full before:bg-[#93FF18] before:shadow-[0_0_12px_rgb(147_255_24/.35)]';
/** Section h2 (every block but the two demo kickers and the closing band). */
export const SECTION_TITLE = 'mt-3 text-[clamp(1.875rem,1.25rem+2.5vw,3rem)] font-bold leading-[1.04] tracking-[-.032em] text-white text-balance break-words [@media(width<22rem)]:[overflow-wrap:anywhere] [&_em]:not-italic [&_em]:text-[#93FF18] [&_em]:[text-shadow:0_0_26px_rgb(147_255_24/.32)]';
export const SECTION_LEAD = 'mt-4 text-[length:clamp(1.0625rem,1rem+.3vw,1.1875rem)] leading-[1.55] text-[#CBD5E1] max-w-[36em] break-words [@media(width<22rem)]:[overflow-wrap:anywhere]';
export const SECTION = 'relative isolate py-[clamp(3.5rem,2rem+5.5vw,7rem)]';
export const SECTION_BAND = 'bg-[linear-gradient(180deg,rgb(255_255_255/.028),rgb(255_255_255/0)_30%,rgb(255_255_255/.022))] shadow-[inset_0_1px_0_rgb(255_255_255/.055),inset_0_-1px_0_rgb(255_255_255/.045)]';
export const CONTAINER = 'w-full max-w-[90rem] mx-auto px-[min(var(--gutter),7vw)]';
export const SECTION_HEAD_CENTER = 'relative max-w-[46rem] mb-[clamp(2rem,1.2rem+2.6vw,3.25rem)] mx-auto text-center [&>p:first-child]:justify-center';
/* `.btn` is a kit component class: the utilities that override it carry `!` because, unlike the
   source's layer order, a same-specificity module rule would otherwise win. */
export const CTA_LARGE = 'btn btn-primary btn--lg !max-w-full !whitespace-normal !text-center';

const RICH_TAGS: Record<string, ReactElement> = { b: <b />, em: <em />, br: <br /> };

/** A translated sentence whose inline markup is limited to <b>, <em> and <br/> (plus any extra slots). */
export function Rich({ i18nKey, values, components }: { i18nKey: string; values?: Record<string, unknown>; components?: Record<string, ReactElement> }) {
  return <Trans ns={NAMESPACE} i18nKey={i18nKey} values={values} components={{ ...RICH_TAGS, ...components }} />;
}

/** Both pictures ship AVIF + WebP next to each other under public/assets/images/trial/v3b. */
export function Picture({ name, width, height, alt, sizes, widths, className, loading = 'lazy' }: { name: string; width: number; height: number; alt: string; sizes?: string; widths?: number[]; className?: string; loading?: 'lazy' | 'eager' }) {
  const list = widths ?? [width];
  const srcSet = (ext: string) => list.map((size) => `${IMG}/${name}-${size}.${ext} ${size}w`).join(', ');
  const largest = list[list.length - 1];
  return (
    <picture>
      <source type='image/avif' srcSet={srcSet('avif')} sizes={sizes} />
      <img className={className} src={`${IMG}/${name}-${largest}.webp`} srcSet={srcSet('webp')} sizes={sizes} alt={alt} width={width} height={height} loading={loading} decoding='async' />
    </picture>
  );
}

interface PauseChipProps {
  paused: boolean;
  onToggle: () => void;
  pauseAria: string;
  resumeAria: string;
  pauseLabel: string;
  resumeLabel: string;
  controls?: string;
  className?: string;
}

/** The design's "Pausar / Retomar" chip: a 44px hit area around a 32px pill, the name always says what it will do. */
export function PauseChip({ paused, onToggle, pauseAria, resumeAria, pauseLabel, resumeLabel, controls, className = '' }: PauseChipProps) {
  return (
    <button className={`group -my-1.5 inline-flex min-h-11 flex-none cursor-pointer items-center rounded-full border-0 bg-transparent p-0 text-[#9AA3B5] [font:inherit] [@media(width<19.5rem)]:min-w-0 [@media(width<19.5rem)]:shrink ${className}`} type='button' aria-pressed={paused} aria-label={paused ? resumeAria : pauseAria} aria-controls={controls} onClick={onToggle}>
      <span className='inline-flex min-h-8 items-center gap-[.4rem] rounded-full border border-white/10 bg-white/[.04] px-[.7rem] py-[.3rem] text-[.75rem] font-semibold leading-none tracking-[.02em] [transition:var(--transition-ui)] group-hover:border-white/20 group-hover:bg-white/[.08] group-hover:text-white [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:px-[.9rem]'>
        <svg className='group-aria-pressed:hidden' width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' aria-hidden='true' focusable='false'>
          <path d='M8.5 5.5v13M15.5 5.5v13' />
        </svg>
        <svg className='hidden group-aria-pressed:block' width='15' height='15' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
          <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
        </svg>
        <span>{paused ? resumeLabel : pauseLabel}</span>
      </span>
    </button>
  );
}

/** Background layers of a section (grid, halos, map) — decorative, painted by the module CSS. */
export function SectionBackdrop({ children }: { children: ReactNode }) {
  return (
    <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
      {children}
    </div>
  );
}

/** An internal link that opens in a new tab and says so to screen readers (the text comes from Trans). */
export function NewTabLink({ href, children }: { href: string; children?: ReactNode }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <Link href={href} target='_blank' rel='noopener'>
      {children}
      <span className='sr-only'>{t('common.new_tab')}</span>
    </Link>
  );
}
